import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Activity } from "lucide-react";
import { PRESET_TESTS, FIELDS, TEST_CONFIGS, sumSubscores } from "./testConfig";

function blank() {
  return {
    test_name: PRESET_TESTS[0], date: new Date().toISOString().slice(0, 10),
    raw_score: "", standard_score: "", sten: "", percentile: "",
    window: "", risk_band: "", subscores: {}, notes: "",
  };
}

export default function TestForm({ studentId, onSaved }) {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const config = TEST_CONFIGS[form.test_name];

  const setSub = (label, val) => setForm((f) => ({ ...f, subscores: { ...f.subscores, [label]: val } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { student_id: studentId, ...form };
      payload.subscores = Object.fromEntries(Object.entries(form.subscores).filter(([, v]) => v !== "" && v != null));
      await api.post("/standardized-tests", payload);
      toast.success("Test result saved");
      setForm({ ...blank(), test_name: form.test_name, window: form.window });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
      <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Activity size={20} className="text-[#6C8EBF]" /> Record a test / score</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Test</span>
          <select data-testid="test-name-input" value={customMode ? "__custom__" : form.test_name}
            onChange={(e) => {
              if (e.target.value === "__custom__") { setCustomMode(true); setForm({ ...form, test_name: "", subscores: {}, window: "", risk_band: "" }); }
              else { setCustomMode(false); setForm({ ...form, test_name: e.target.value, subscores: {}, window: "", risk_band: "" }); }
            }}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white">
            {PRESET_TESTS.map((t) => <option key={t} value={t}>{t}</option>)}
            <option value="__custom__">Custom test…</option>
          </select>
          {customMode && (
            <input data-testid="test-custom-name-input" required placeholder="Enter test name" value={form.test_name}
              onChange={(e) => setForm({ ...form, test_name: e.target.value })}
              className="mt-2 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
          )}
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Date</span>
          <input type="date" data-testid="test-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
        </label>

        {config?.window && (
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Assessment window</span>
            <select data-testid="test-window-input" value={form.window} onChange={(e) => setForm({ ...form, window: e.target.value })}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white">
              <option value="">Select…</option>
              {config.window.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </label>
        )}
        {config?.riskBands && (
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Overall risk band</span>
            <select data-testid="test-risk-input" value={form.risk_band} onChange={(e) => setForm({ ...form, risk_band: e.target.value })}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white">
              <option value="">Select…</option>
              {config.riskBands.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}

        {config?.subscores
          ? config.subscores.map((s) => {
            const tid = `sub-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-input`;
            return (
              <label key={s.label} className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{s.label}{s.max ? ` / ${s.max}` : ""}</span>
                {s.options ? (
                  <select data-testid={tid} value={form.subscores[s.label] || ""} onChange={(e) => setSub(s.label, e.target.value)}
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white">
                    <option value="">—</option>
                    {s.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={s.text ? "text" : "number"} data-testid={tid}
                    value={form.subscores[s.label] || ""} onChange={(e) => setSub(s.label, e.target.value)}
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
                )}
              </label>
            );
          })
          : FIELDS.map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
              <input data-testid={`test-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white" />
            </label>
          ))}
      </div>

      {config?.showTotal && (
        <p className="mt-3 text-sm text-slate-500">Total: <b className="text-slate-800" data-testid="mist-total">{sumSubscores(form.subscores)}</b></p>
      )}

      <input data-testid="test-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)"
        className="mt-3 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#81B29A] focus:bg-white" />
      <button data-testid="save-test-btn" disabled={saving}
        className="mt-4 bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add result
      </button>
    </form>
  );
}
